// Filtro "Videos / Todo" de la columna de resultados —
// ADR-2026-09-08-creador-de-video-filtra-por-tipo-de-contenido.
//
// Creador de video es un modo que SÓLO hace videos, pero su columna muestra todo lo que
// generó la empresa (ADR-2026-09-06-las-tandas-se-ven-en-todos-los-modos): abrirlo para
// ver un video y encontrar cuarenta fotos es no encontrar nada. El filtro arranca en
// "videos" y el usuario abre a "todo" cuando quiere.
//
// El filtro se aplica en DOS lugares —la consulta a la base y las celdas ya armadas— y
// por eso las dos reglas viven acá juntas: si el `where` de PostgREST y el predicado del
// cliente divergen, la grilla esconde filas que el hook trajo como video (o al revés) y
// quedan huecos que nadie puede explicar.

/** Qué se está mirando. `'videos'` es el default de Creador de video. */
export type FiltroContenido = 'videos' | 'todo';

/**
 * El `or` de PostgREST que deja pasar sólo los videos, palabra por palabra la misma
 * regla que `esVideoDeFila`.
 *
 * Se filtra en la BASE y no después de traer: la columna pide de a 18 filas y los videos
 * son una minoría del historial, así que un filtro en el cliente devolvía páginas enteras
 * vacías —el usuario scrolleaba contra la nada mientras la paginación seguía trayendo
 * fotos que descartaba—.
 *
 * `*` es el comodín de `ilike` en la sintaxis de `or` (PostgREST lo traduce a `%`).
 */
export const FILTRO_VIDEO_PG = 'tipo.in.(video,video_pro),file_name.ilike.*.mp4';

/** La fila de `imagenes_generadas`, en lo poco que hace falta para decidir si es video. */
export interface FilaConTipo {
  tipo: string | null;
  file_name: string | null;
}

/**
 * ¿La fila es un video? El mismo criterio que usa el historial: por `tipo`, con la
 * extensión como respaldo — hay filas viejas de flujos que no lo guardaron.
 */
export function esVideoDeFila(fila: FilaConTipo): boolean {
  return (
    fila.tipo === 'video' ||
    fila.tipo === 'video_pro' ||
    !!fila.file_name?.toLowerCase().endsWith('.mp4')
  );
}

/**
 * Recorta las celdas de la grilla según el filtro.
 *
 * Es genérico sobre `{ esVideo }` a propósito: se aplica a las celdas YA armadas —las de
 * la tanda en curso y las del historial en el mismo array—, así que esconde también los
 * recuadros "generando" de una tanda de imágenes de otro modo. Es lo correcto: si el
 * filtro dice "videos", una tanda de fotos corriendo en Modelo no tiene por qué aparecer.
 *
 * Con `'todo'` devuelve el array RECIBIDO, no una copia: aguas abajo lo consumen varios
 * `useMemo` y una identidad nueva por render los invalidaría a todos sin motivo.
 */
export function filtrarPorContenido<T extends { esVideo: boolean }>(
  celdas: T[],
  filtro: FiltroContenido,
): T[] {
  return filtro === 'videos' ? celdas.filter((c) => c.esVideo) : celdas;
}
